/**
 * Client workspace · Creative Library tab — wired against
 * `list-creative-assets?client_id=`, `get-creative-asset?asset_id=`,
 * `list-creative-asset-matches?asset_id=`.
 *
 * Per `docs/frontend/035-frontend-app-shell-plan.md §6.6`. The wire
 * deliberately omits sensitive fields like rationale / pattern / score
 * (Task 019 §17 redaction); we render only the columns the endpoint
 * surfaces. Mutation surfaces (manual_link match, archive, recommendation
 * review) are deferred — action buttons render `.btn-coming-soon`.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { isApiError } from '../../api/client';
import {
  getCreativeAsset,
  listCreativeAssetMatches,
  listCreativeAssets,
} from '../../api/endpoints';
import type {
  CreativeAssetMatchMethod,
  CreativeAssetMatchRow,
  CreativeAssetMatchSource,
  CreativeAssetRow,
  CreativeAssetType,
} from '../../api/types';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../../components/states';

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly assets: ReadonlyArray<CreativeAssetRow> }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

type DetailState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly asset: CreativeAssetRow;
      readonly matches: ReadonlyArray<CreativeAssetMatchRow>;
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

const TYPE_FILTERS: ReadonlyArray<{
  readonly value: CreativeAssetType | 'all';
  readonly label: string;
}> = [
  { value: 'all', label: 'الكل' },
  { value: 'image', label: 'صور' },
  { value: 'video', label: 'فيديو' },
  { value: 'audio', label: 'صوت' },
  { value: 'copy_block', label: 'نصوص' },
  { value: 'document', label: 'مستندات' },
  { value: 'multipart', label: 'متعدد' },
];

export function CreativePage() {
  const { clientId } = useParams();
  const [typeFilter, setTypeFilter] = useState<CreativeAssetType | 'all'>('all');
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setList({ status: 'loading' });
    listCreativeAssets(clientId, { signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setList({ status: 'ready', assets: data.assets });
        if (data.assets.length > 0 && data.assets[0]) {
          setSelectedId((prev) =>
            prev && data.assets.some((a) => a.id === prev) ? prev : data.assets[0]!.id,
          );
        } else {
          setSelectedId(null);
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setList({ status: 'not-found' });
          return;
        }
        setList({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, reloadKey]);

  useEffect(() => {
    if (!selectedId) {
      setDetail({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setDetail({ status: 'loading' });
    Promise.all([
      getCreativeAsset(selectedId, { signal: ctrl.signal }),
      listCreativeAssetMatches(selectedId, { signal: ctrl.signal }),
    ])
      .then(([assetData, matchesData]) => {
        if (ctrl.signal.aborted) return;
        setDetail({
          status: 'ready',
          asset: assetData.asset,
          matches: matchesData.matches,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setDetail({ status: 'not-found' });
          return;
        }
        setDetail({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [selectedId]);

  if (!clientId) return null;

  if (list.status === 'loading') return <LoadingState title="جاري تحميل الأصول الإبداعية…" />;
  if (list.status === 'not-found')
    return (
      <NotFoundState
        title="لا يمكن الوصول إلى مكتبة هذا العميل"
        description="إما أن العميل غير موجود، أو لا تملك صلاحية فتح المكتبة."
      />
    );
  if (list.status === 'error')
    return (
      <ErrorState
        title="تعذر تحميل المكتبة"
        error={list.error}
        onRetry={() => setReloadKey((n) => n + 1)}
      />
    );

  const filtered =
    typeFilter === 'all' ? list.assets : list.assets.filter((a) => a.asset_type === typeFilter);

  return (
    <div className="stack-md">
      <div className="toolbar-row">
        {TYPE_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={typeFilter === f.value ? 'chip on' : 'chip'}
            onClick={() => setTypeFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {list.assets.length === 0 ? (
        <EmptyState
          title="لا توجد أصول إبداعية بعد"
          description="ستظهر هنا الأصول بمجرد رفع أول صورة أو فيديو لهذا العميل."
        />
      ) : filtered.length === 0 ? (
        <EmptyState title="لا أصول مطابقة" description="جرّب فلتر آخر." />
      ) : (
        <div className="workspace-split">
          <aside className="workspace-list">
            <div className="sec-title">
              <h2>الأصول</h2>
              <span className="meta">{filtered.length}</span>
            </div>
            <ul className="row-list">
              {filtered.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    className={a.id === selectedId ? 'row-card row-card-active' : 'row-card'}
                    onClick={() => setSelectedId(a.id)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <strong style={{ fontSize: 13 }}>{a.canonical_name}</strong>
                      <span className="pill pill-info">{assetTypeLabel(a.asset_type)}</span>
                    </div>
                    <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                      {a.mime_type ?? '—'}
                      {a.byte_size ? ` · ${formatBytes(a.byte_size)}` : ''}
                      {a.width && a.height ? ` · ${a.width}×${a.height}` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
          <section className="workspace-detail">
            <CreativeDetail state={detail} />
          </section>
        </div>
      )}
    </div>
  );
}

function CreativeDetail({ state }: { readonly state: DetailState }) {
  if (state.status === 'idle')
    return (
      <EmptyState
        title="اختر أصلاً لعرض التفاصيل"
        description="اضغط أي بطاقة من القائمة على اليمين."
      />
    );
  if (state.status === 'loading') return <LoadingState title="جاري تحميل تفاصيل الأصل…" />;
  if (state.status === 'not-found')
    return <NotFoundState title="الأصل غير متاح" description="ربما تم أرشفته." />;
  if (state.status === 'error')
    return <ErrorState title="تعذر تحميل تفاصيل الأصل" error={state.error} />;

  const { asset, matches } = state;
  return (
    <div className="stack-md">
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>{asset.canonical_name}</h2>
          <span className="meta">{assetTypeLabel(asset.asset_type)}</span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 14,
          }}
        >
          <Field label="نوع MIME" value={asset.mime_type} />
          <Field label="الحجم" value={asset.byte_size ? formatBytes(asset.byte_size) : null} />
          <Field
            label="الأبعاد"
            value={asset.width && asset.height ? `${asset.width}×${asset.height}` : null}
          />
          <Field
            label="المدة"
            value={asset.duration_ms ? `${(asset.duration_ms / 1000).toFixed(1)} ث` : null}
          />
          <Field label="تم الرفع" value={new Date(asset.created_at).toLocaleString('ar-SA')} />
          <Field
            label="مؤرشف"
            value={asset.archived_at ? new Date(asset.archived_at).toLocaleString('ar-SA') : null}
          />
        </div>
        {asset.aliases.length > 0 ? (
          <div style={{ marginTop: 14 }}>
            <span className="lbl">الأسماء البديلة (Aliases)</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {asset.aliases.map((alias) => (
                <span
                  key={alias}
                  className="pill pill-info"
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}
                >
                  {alias}
                </span>
              ))}
            </div>
          </div>
        ) : null}
        {asset.checksum_sha256 ? (
          <p
            className="tiny"
            style={{
              color: 'var(--muted)',
              marginTop: 14,
              fontFamily: 'var(--font-mono)',
              direction: 'ltr',
            }}
          >
            sha256: {asset.checksum_sha256.slice(0, 12)}…{asset.checksum_sha256.slice(-12)}
          </p>
        ) : null}
        <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
          <button type="button" className="btn-coming-soon" disabled>
            ربط يدوي (قريباً)
          </button>
          <button type="button" className="btn-coming-soon" disabled>
            أرشفة (قريباً)
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>الربط مع الحملات والمحتوى</h2>
          <span className="meta">{matches.length} ربط</span>
        </div>
        {matches.length === 0 ? (
          <EmptyState
            title="لا توجد ارتباطات"
            description="ستظهر هنا روابط الأصل مع الحملات أو المحتوى عند توفّرها."
          />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>المصدر</th>
                <th>اسم خارجي</th>
                <th>طريقة الربط</th>
                <th>الثقة</th>
                <th>تم الربط</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => (
                <tr key={m.id}>
                  <td>
                    <span className="name">{matchSourceLabel(m.match_source)}</span>
                    {m.match_source_id ? (
                      <div
                        className="sub"
                        style={{ fontFamily: 'var(--font-mono)', direction: 'ltr' }}
                      >
                        {m.match_source_id.slice(0, 8)}…
                      </div>
                    ) : null}
                  </td>
                  <td>{m.match_source_external_name}</td>
                  <td>{matchMethodLabel(m.match_method)}</td>
                  <td className="num">
                    {m.match_confidence !== null
                      ? `${(m.match_confidence * 100).toFixed(0)}%`
                      : '—'}
                  </td>
                  <td>{new Date(m.created_at).toLocaleDateString('ar-SA')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function assetTypeLabel(type: CreativeAssetType): string {
  switch (type) {
    case 'image':
      return 'صورة';
    case 'video':
      return 'فيديو';
    case 'audio':
      return 'صوت';
    case 'copy_block':
      return 'نص';
    case 'document':
      return 'مستند';
    case 'multipart':
      return 'متعدد';
    default:
      return type;
  }
}

function matchSourceLabel(source: CreativeAssetMatchSource): string {
  switch (source) {
    case 'paid_media_ad':
      return 'إعلان مدفوع';
    case 'paid_media_ad_set':
      return 'مجموعة إعلانية';
    case 'paid_media_campaign':
      return 'حملة مدفوعة';
    case 'content_item':
      return 'قطعة محتوى';
    case 'content_variant':
      return 'متغير محتوى';
    case 'external_text':
      return 'نص خارجي';
    default:
      return source;
  }
}

function matchMethodLabel(method: CreativeAssetMatchMethod): string {
  switch (method) {
    case 'exact_name':
      return 'اسم مطابق';
    case 'alias':
      return 'اسم بديل';
    case 'fuzzy':
      return 'مطابقة تقريبية';
    case 'manual_link':
      return 'ربط يدوي';
    default:
      return method;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body">{value && value.length > 0 ? value : '—'}</span>
    </div>
  );
}
